import useDateFormatters from "@hooks/useDateFormatters"

import { faIcon } from "@utils/faIcon"

import { BULLET_CHARACTER } from "@particles/Bullet/Bullet"

import { Avatar } from "@atoms"

import {
    EstimateCountBadge,
    InvoiceCountBadge,
    IssueDateBadge,
    ItemCountBadge,
    JobCountBadge,
    LabelsBadges,
    LastModifiedBadge,
    TotalBadge,
} from "@molecules/ObjectCard/Attributes/AttributeBadges"
import ObjectCard from "@molecules/ObjectCard/ObjectCard"
import { shouldShowAttribute } from "@molecules/ObjectCard/ObjectCards/ObjectCards.utils"
import ObjectStatusBadge from "@molecules/ObjectStatusBadge/ObjectStatusBadge"

import { PurchaseOrderCardProps } from "./PurchaseOrderCard.types"

const vendorIcon = faIcon({ name: "store", style: "solid" })

type PurchaseOrderTimestampKey =
    | "created_at"
    | "last_sent_at"
    | "last_acknowledged_at"
    | "last_in_transit_at"
    | "last_delivered_at"
    | "last_stocked_at"
    | "last_paid_at"
    | "last_cancelled_at"
    | "last_status_transition_time"

const STATUS_TRANSITION: Record<
    PurchaseOrder["status_label"],
    { verb: string; timestampKey: PurchaseOrderTimestampKey }
> = {
    Draft: { verb: "Created", timestampKey: "created_at" },
    Unsent: { verb: "Created", timestampKey: "created_at" },
    Sent: { verb: "Sent", timestampKey: "last_sent_at" },
    Acknowledged: { verb: "Acknowledged", timestampKey: "last_acknowledged_at" },
    "In Transit": { verb: "Shipped", timestampKey: "last_in_transit_at" },
    Unstocked: { verb: "Delivered", timestampKey: "last_delivered_at" },
    Unpaid: { verb: "Stocked", timestampKey: "last_stocked_at" },
    Paid: { verb: "Paid", timestampKey: "last_paid_at" },
    Cancelled: { verb: "Cancelled", timestampKey: "last_cancelled_at" },
}

export default function PurchaseOrderCard(props: PurchaseOrderCardProps) {
    const { purchaseOrder, displayedAttributes, onClick, isLoading = false, className, ...errorProps } = props

    const { formatDatetimeTooltip } = useDateFormatters()

    const { verb, timestampKey } = STATUS_TRANSITION[purchaseOrder.status_label]
    const statusTooltip = formatDatetimeTooltip(
        verb,
        purchaseOrder[timestampKey] ?? purchaseOrder.last_status_transition_time ?? purchaseOrder.created_at,
    )

    // Body line: "Main Warehouse ・ 123 Main St, San Francisco, CA 98765"; the tooltip splits it across two lines.
    const inventoryLocationParts = [
        purchaseOrder.inventory_location_name,
        purchaseOrder.inventory_location_address,
    ].filter(Boolean)
    const inventoryLocationText = inventoryLocationParts.join(` ${BULLET_CHARACTER} `)
    const inventoryLocationTooltip = inventoryLocationParts.join("\n")

    const showSubtotal = shouldShowAttribute(displayedAttributes, "subtotal") && purchaseOrder.subtotal != null
    const showIssued = shouldShowAttribute(displayedAttributes, "date_issued") && Boolean(purchaseOrder.date_issued)
    const showItemCount = shouldShowAttribute(displayedAttributes, "item_count")
    const showEstimateCount =
        shouldShowAttribute(displayedAttributes, "associated_estimates") &&
        purchaseOrder.associated_estimates.length > 0
    const showJobCount =
        shouldShowAttribute(displayedAttributes, "associated_jobs") && purchaseOrder.associated_jobs.length > 0
    const showInvoiceCount =
        shouldShowAttribute(displayedAttributes, "associated_invoices") && purchaseOrder.associated_invoices.length > 0
    const showLastModified =
        shouldShowAttribute(displayedAttributes, "last_modified_at") && Boolean(purchaseOrder.last_modified_at)
    const showLabels = shouldShowAttribute(displayedAttributes, "labels") && purchaseOrder.labels.length > 0

    const hasAttributes =
        showSubtotal ||
        showIssued ||
        showItemCount ||
        showEstimateCount ||
        showJobCount ||
        showInvoiceCount ||
        showLastModified ||
        showLabels

    return (
        <ObjectCard onClick={onClick} isLoading={isLoading} className={className} {...errorProps}>
            <ObjectCard.Header
                id={purchaseOrder.custom_id ?? String(purchaseOrder.id)}
                showSeenIcon={Boolean(purchaseOrder.last_viewed)}
                statusBadge={
                    <ObjectStatusBadge
                        objectType="purchase_order"
                        status={purchaseOrder.status_label}
                        size="md"
                        tooltip={statusTooltip}
                    />
                }
            />
            <ObjectCard.Body
                avatar={<Avatar variant="object" icon={vendorIcon} size="xl" />}
                primaryText={purchaseOrder.vendor_name}
                secondaryText={inventoryLocationText}
                secondaryTextTooltip={inventoryLocationTooltip}
            />
            {(isLoading || hasAttributes) && (
                <ObjectCard.Attributes>
                    {showSubtotal && <TotalBadge value={purchaseOrder.subtotal} label="Subtotal" />}
                    {showIssued && <IssueDateBadge date={purchaseOrder.date_issued} />}
                    {showItemCount && <ItemCountBadge count={purchaseOrder.item_count} />}
                    {showEstimateCount && (
                        <EstimateCountBadge
                            count={purchaseOrder.associated_estimates.length}
                            tooltip={purchaseOrder.associated_estimates.join("\n")}
                        />
                    )}
                    {showJobCount && (
                        <JobCountBadge
                            count={purchaseOrder.associated_jobs.length}
                            tooltip={purchaseOrder.associated_jobs.join("\n")}
                        />
                    )}
                    {showInvoiceCount && (
                        <InvoiceCountBadge
                            count={purchaseOrder.associated_invoices.length}
                            tooltip={purchaseOrder.associated_invoices.join("\n")}
                        />
                    )}
                    {showLastModified && <LastModifiedBadge date={purchaseOrder.last_modified_at} />}
                    {showLabels && <LabelsBadges labels={purchaseOrder.labels} />}
                </ObjectCard.Attributes>
            )}
        </ObjectCard>
    )
}
